/* =====================================
   CUSTOM PRODUCT OPTIONS ("Pick your own flowers" checkboxes) -
   HIDDEN FIELD SYNC SAFETY NET
   FIX (03-Sep-2026): the "Best Custom Product Options" app renders the
   visible checkbox list a customer actually clicks completely separately
   from the hidden form field that actually gets submitted to the cart
   (that field's own source checkboxes sit in a permanently hidden
   <fieldset> template elsewhere in the same form). The app's own script
   is responsible for mirroring clicks on the visible checkboxes into
   that hidden field, but this mirroring does not always fire - verified:
   a checkbox can show fully checked while the hidden field stays empty,
   so the item adds to cart with no flower selection attached at all,
   silently. Customer-reported symptom on Subscribe to Cut Flowers -
   Luxury: picked exactly 4 flowers, added to cart, no flower selection
   appeared on the cart line item.

   FIX v2: tried matching visible-to-hidden-template checkboxes by
   (fuzzily normalized) option text. Works for Luxury, not for older
   products - confirmed on Premium the hidden template's own text has
   drifted from the visible text beyond formatting, e.g. hidden says
   "Hydrangeas (Bunch of 2)" while visible says "Hydrangea (bunch of 2
   stems)" - genuinely different wording, not just case/plural, so no
   text-matching scheme can bridge it reliably.

   FIX v3: the hidden field's NAME also isn't consistent across products
   - Luxury uses `properties[infinite_options_8]`, Premium uses a
   merchant-chosen `properties[Flowers picked]`. Target the shared DOM
   *structure* instead (a <fieldset> holding one hidden input plus the
   template checkboxes), not the field's name or its checkboxes' text.

   FIX v4: drop text matching entirely. Every product checked so far
   renders exactly one such hidden fieldset/field pair per form, and
   exactly one visible checkbox group outside any fieldset - so mirror
   every checked, non-fieldset checkbox's value straight into that one
   hidden field, no text comparison needed.

   FIX v5 (03-Sep-2026): v4 correctly gets the selection into the
   Shopify cart's line-item property every time (verified repeatedly,
   including with realistic manual clicks) - but a real test order
   (#TF45415) still showed no flower selection on the order page.
   Root cause: this store's checkout is handled by Shopflo, a
   third-party checkout replacement, which only forwards a merchant-
   configured allow-list of custom fields into the final order -
   "Delivery Date"/"Personal Message" were clearly registered with
   Shopflo at setup time, but the flower-picker field never was, so
   Shopflo silently drops it regardless of what we do to the line item.
   Route around this via the one pathway already proven to survive
   Shopflo's handoff: cart-level attributes (the same mechanism
   saveDeliveryDetailsToCart() below uses for Delivery Date), rather
   than a line-item property.

   FIX v6 (03-Sep-2026): v5's key was "Flowers picked - <Product Name>"
   (unique per product) - confirmed via a second real order (#TF45416)
   that Shopflo dropped it anyway, same as the line-item property before
   it. Shopflo's allow-list is keyed on the exact, fixed attribute name
   (that's how Delivery-Date/Delivery-Time/Delivery-Location/Personal
   Message survive - all fixed names, none of them per-product), so a
   name that varies per product could never be added to that allow-list
   as a single entry. Switched to one fixed key, "Flowers picked",
   matching that convention. This still requires Shopflo's own dashboard
   to be configured to forward "Flowers picked" the same way those other
   fields were - the theme can only ever get as far as setting the cart
   attribute correctly, it cannot make Shopflo forward it.

   FIX v7 (03-Sep-2026): that second order also turned out to have no
   flower-selection value AT ALL to forward - retesting v6 on this draft
   found the sync itself silently no-ops. Root cause: `liveBoxes` was
   scoped to `form.querySelectorAll(...)`, but the visible widget a
   customer actually clicks (the "Best Custom Product Options" app's own
   #bcpo container) is not a DOM descendant of the <form> at all - it
   renders as a sibling next to it (confirmed via the live DOM: the 9
   visible flower checkboxes are all inside #bcpo, while the <form> only
   contains the always-hidden template fieldset). Scoping the search to
   the form therefore always found zero live boxes; any earlier-observed
   success was a timing fluke, not a working fix. Now searches inside
   #bcpo specifically (with a document-wide fallback if that container
   isn't found), which is where the app actually puts its checkboxes on
   every product using it.

   FIX v8 (03-Sep-2026): v7 alone still didn't fix it - retesting found
   the hidden field never updates on a checkbox click at all. Root cause:
   the 'change' listener resolved the form via
   `e.target.closest('form[action*="/cart/add"]')`, starting from the
   checkbox itself. `closest()` only walks up the DOM tree - and since
   the checkbox is a *sibling* of the form, not a descendant of it (the
   same fact behind FIX v7), that walk never reaches the form, `form` is
   always null, and the sync silently never runs on checkbox clicks. This
   is the actual reason nothing has worked all day; FIX v7's #bcpo
   scoping was a real bug too, but moot as long as this one made the
   whole handler a no-op. Fixed by resolving the form independently of
   the click target - `document.querySelector('form[action*="/cart/add"]')`
   directly, since a product page only ever has one such form - instead
   of walking up from the checkbox.
===================================== */
(function () {
    function normalize(value) {
        return (value || '')
            .toLowerCase()
            .split(/[^a-z0-9]+/i)
            .filter(Boolean)
            .map(function (word) { return word.replace(/s$/, ''); })
            .join(' ');
    }

    function pushFlowerSelectionCartAttribute(value) {
        if (!window.Shopify || !window.Shopify.routes || !window.Shopify.routes.root) return;
        var formData = new FormData();
        formData.append('attributes[Flowers picked]', value || '');
        fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            body: formData
        }).catch(function () {});
    }

    function syncInfiniteOptionsFields(form) {
        var candidateFieldsets = Array.prototype.filter.call(
            form.querySelectorAll('fieldset'),
            function (fs) {
                return fs.querySelector('input[type="hidden"]') && fs.querySelector('input[type="checkbox"]');
            }
        );
        if (!candidateFieldsets.length) return;

        var widgetScope = document.getElementById('bcpo') || document;

        if (candidateFieldsets.length === 1) {
            var templateFieldset = candidateFieldsets[0];
            var hiddenField = templateFieldset.querySelector('input[type="hidden"]');

            var liveBoxes = Array.prototype.filter.call(
                widgetScope.querySelectorAll('input[type="checkbox"]'),
                function (box) { return !templateFieldset.contains(box) && box.name !== 'addons'; }
            );
            if (!liveBoxes.length) return;

            var checkedValues = liveBoxes
                .filter(function (box) { return box.checked; })
                .map(function (box) { return box.value; });

            hiddenField.value = checkedValues.join(',');
            pushFlowerSelectionCartAttribute(hiddenField.value);
            return;
        }

        // More than one option group on this page - fall back to fuzzy text
        // matching per group, since some disambiguation is unavoidable here.
        candidateFieldsets.forEach(function (templateFieldset) {
            var hiddenField = templateFieldset.querySelector('input[type="hidden"]');
            var templateBoxes = templateFieldset.querySelectorAll('input[type="checkbox"]');

            var templateValues = Array.prototype.map.call(templateBoxes, function (box) {
                return normalize(box.value);
            });

            var liveBoxes = Array.prototype.filter.call(
                widgetScope.querySelectorAll('input[type="checkbox"]'),
                function (box) {
                    return !templateFieldset.contains(box) && templateValues.indexOf(normalize(box.value)) !== -1;
                }
            );
            if (!liveBoxes.length) return;

            var checkedValues = liveBoxes
                .filter(function (box) { return box.checked; })
                .map(function (box) { return box.value; });

            hiddenField.value = checkedValues.join(',');
            pushFlowerSelectionCartAttribute(hiddenField.value);
        });
    }

    document.addEventListener('change', function (e) {
        if (!e.target || e.target.type !== 'checkbox') return;
        var form = document.querySelector('form[action*="/cart/add"]');
        if (form) syncInfiniteOptionsFields(form);
    }, true);

    document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form && form.matches && form.matches('form[action*="/cart/add"]')) {
            syncInfiniteOptionsFields(form);
        }
    }, true);

    document.addEventListener('click', function (e) {
        var btn = e.target.closest('button[type="submit"], [name="add"], #flo-buy-now-button');
        if (!btn) return;
        var form = document.querySelector('form[action*="/cart/add"]');
        if (form) syncInfiniteOptionsFields(form);
    }, true);
})();

/* =====================================
   CITY-MISMATCHED COLLECTION REDIRECT
   FIX (01-Sep-2026): each city has its own dedicated collection URL
   (bouquets vs bouquets-delhi vs bouquets-hyderabad, etc). A visitor
   whose selected city is Delhi/Hyderabad can still land directly on
   the generic Bangalore URL (old links, search results, bookmarks).
   The header city label still reads correctly via selectCity() below,
   but the page content underneath stays Bangalore's - e.g.
   /collections/bouquets shows "Shop Flower Bouquets in Bangalore"
   while the city selector says Delhi. Send Delhi/Hyderabad visitors
   to their city's equivalent collection instead.
===================================== */
(function () {
    var CITY_COLLECTION_REDIRECTS = {
        'bouquets': { delhi: 'bouquets-delhi', hyderabad: 'bouquets-hyderabad' },
        'cut-flowers': { delhi: 'cut-flowers-delhi', hyderabad: 'cut-flowers-hyderabad' },
        'flower-baskets': { delhi: 'flower-baskets-delhi', hyderabad: 'flower-baskets-hyderabad' },
        'subscribe': { delhi: 'subscription-delhi', hyderabad: 'flower-subscriptions-hyderabad' },
        'bestselling-flower-bouquets': { delhi: 'shop-our-bestselling-fresh-flower-bouquets-delhi', hyderabad: 'shop-our-bestselling-fresh-flower-bouquets-hyderabad' }
    };

    var match = window.location.pathname.match(/^\/collections\/([^\/]+)\/?$/);
    if (!match) return;

    var targetsByCity = CITY_COLLECTION_REDIRECTS[match[1]];
    if (!targetsByCity) return;

    var savedCityId = window.localStorage ? window.localStorage.getItem('selectedCityId') : null;
    var targetHandle = savedCityId && targetsByCity[savedCityId];
    if (!targetHandle) return;

    window.location.replace('/collections/' + targetHandle + window.location.search);
})();

function setCityCookie(city) {
    document.cookie = "userCity=" + city + "; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/";
}

function getCityCookie() {
    var name = "userCity=";
    var decodedCookie = decodeURIComponent(document.cookie);
    var cookieArray = decodedCookie.split(';');
    for (var i = 0; i < cookieArray.length; i++) {
        var cookie = cookieArray[i];
        while (cookie.charAt(0) == ' ') {
            cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) == 0) {
            return cookie.substring(name.length, cookie.length);
        }
    }
    return "";
}


if (Shopify.designMode) {
    setCityCookie('Bangalore');
}

function isCityCookieSet() {
    return document.cookie.split(';').some((cookie) => cookie.trim().startsWith('userCity='));
}

function getCookie(name) {
    if(Shopify.designMode) {
        return "Bangalore"
    }

// Split cookie string and get all individual name=value pairs in an array
let cookieArr = document.cookie.split(";");

// Loop through the array elements
for(let i = 0; i < cookieArr.length; i++) {
    let cookiePair = cookieArr[i].split("=");

    /* Removing whitespace at the beginning of the cookie name
    and compare it with the given string */
    if(name == cookiePair[0].trim()) {
        // Decode the cookie value and return
        return decodeURIComponent(cookiePair[1]);
    }
}

// Return null if not found
return null;
}

if(window.location.pathname.includes('delhi') && !isCityCookieSet() && !Shopify.designMode) {
    setCityCookie("Delhi");
} else if(window.location.pathname.includes('mumbai') && !isCityCookieSet() && !Shopify.designMode) {
    setCityCookie("Mumbai");
} else if(window.location.pathname.includes('hyderabad') && !isCityCookieSet() && !Shopify.designMode) {
    setCityCookie("Hyderabad");
} else if(!isCityCookieSet() && !Shopify.designMode) {
    setCityCookie("Bangalore");
}

// Shopflo: intercept cart icon clicks
// FIX (19-Aug-2026): persist delivery details / Personal Message as cart attributes
// before showing the Shopflo cart, so order-level attributes are always up to date.
document.addEventListener('click', function(e) {
  var trigger = e.target.closest('[aria-controls="cart-drawer"]');
  if (!trigger) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  if (typeof window.saveDeliveryDetailsToCart === 'function') {
    window.saveDeliveryDetailsToCart().finally(function() {
      if (typeof handleFloCartBtn === 'function') handleFloCartBtn();
    });
  } else if (typeof handleFloCartBtn === 'function') {
    handleFloCartBtn();
  }
}, true);

// Shopflo: intercept buy-now
// Save delivery details before Shopflo starts checkout.

document.addEventListener('click', function(e) {

  var btn =
    e.target.closest('#flo-buy-now-button');

  if (!btn) {
    return;
  }

  e.preventDefault();
  e.stopImmediatePropagation();

  console.log(
    'Shopflo Buy Now clicked - saving delivery details first'
  );

  /* -----------------------------------------
     SAVE DELIVERY DETAILS
  ----------------------------------------- */

  if (
    typeof window.saveDeliveryDetailsToCart ===
    'function'
  ) {

    window.saveDeliveryDetailsToCart()

      .then(function() {

        console.log(
          'Delivery details saved. Continuing to Shopflo.'
        );

        /* -----------------------------------------
           CONTINUE WITH SHOPFLO
        ----------------------------------------- */

        if (
          typeof handleFloBuyNowBtn ===
          'function'
        ) {

          handleFloBuyNowBtn(e);

        } else {

          console.error(
            'Shopflo handleFloBuyNowBtn function not found.'
          );

        }

      })

      .catch(function(error) {

        console.error(
          'Buy Now stopped because delivery details could not be saved.',
          error
        );

      });

  } else {

    console.warn(
      'saveDeliveryDetailsToCart() is not available. Continuing with Shopflo.'
    );

    if (
      typeof handleFloBuyNowBtn ===
      'function'
    ) {

      handleFloBuyNowBtn(e);

    }

  }

}, true);

// Shopflo: intercept checkout button clicks
// FIX (19-Aug-2026): persist delivery details / Personal Message as cart attributes
// before proceeding to Shopflo checkout.
document.addEventListener('click', function(e) {
  var btn = e.target.closest('#checkout2');
  if (!btn) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  if (typeof window.saveDeliveryDetailsToCart === 'function') {
    window.saveDeliveryDetailsToCart().finally(function() {
      if (typeof handleFloCheckoutBtn === 'function') handleFloCheckoutBtn();
    });
  } else if (typeof handleFloCheckoutBtn === 'function') {
    handleFloCheckoutBtn();
  }
}, true);

// Shopflo: intercept cart-drawer open/show to show Shopflo cart instead
customElements.whenDefined('cart-drawer').then(function() {
  var CartDrawerClass = customElements.get('cart-drawer');
  if (!CartDrawerClass) return;

  var methodNames = ['open', 'show'];
  methodNames.forEach(function(method) {
    if (typeof CartDrawerClass.prototype[method] === 'function') {
      var original = CartDrawerClass.prototype[method];
      CartDrawerClass.prototype[method] = function() {
        if (typeof handleFloCartBtn === 'function') { handleFloCartBtn(); return; }
        return original.apply(this, arguments);
      };
    }
  });
});

/* =====================================
   GLOBAL CITY SELECTOR
   FIX (19-Aug-2026): the city popup and its click/redirect wiring used
   to live only in custom-product.js, which loads on product pages only.
   Once city selection started redirecting to city-specific landing
   pages, users landed on pages with no popup logic at all - the header
   button looked clickable but did nothing there. Popup markup + this
   wiring now render/run site-wide so the selector always works,
   regardless of what page a city change lands you on. Delivery
   date/slot rendering itself stays in custom-product.js, scoped to
   PDPs, and reads the shared state exposed here (window.cityDeliveryConfig,
   window.currentCityId).
===================================== */

document.addEventListener('DOMContentLoaded', function () {

    window.cityDeliveryConfig = {};

    (function () {

        var configEl = document.getElementById('city-delivery-config');

        if (!configEl) {
            return;
        }

        try {
            window.cityDeliveryConfig = JSON.parse(configEl.textContent) || {};

            /* FIX (20-Aug-2026): metaobject list order isn't guaranteed to
               be chronological, so slots could render in an arbitrary
               sequence that has nothing to do with time of day - and since
               updateSlotStatus() only toggles classes and never reorders
               the DOM, a slot could appear to "jump" position once it
               closes, when really the whole list was never sorted to
               begin with. Sort each city's slots by cutoff time once here
               so the displayed order is always fixed and chronological. */
            Object.keys(window.cityDeliveryConfig).forEach(function (cityId) {
                var cityData = window.cityDeliveryConfig[cityId];
                if (cityData && cityData.slots) {
                    cityData.slots.sort(function (a, b) {
                        return a.cutoff.localeCompare(b.cutoff);
                    });
                }
            });
        } catch (error) {
            console.error('Failed to parse city-delivery-config:', error);
        }

    })();

    window.currentCityId = null;

    var cityLandingUrls = {
        bangalore: '/',
        delhi: '/pages/flower-delivery-delhi',
        hyderabad: '/pages/flower-delivery-hyderabad'
    };

    var popup = document.getElementById('location-popup');
    var changeLocationBtn = document.getElementById('change-location-btn');
    var headerCityTrigger = document.getElementById('header-city-trigger');
    var stickyCityTrigger = document.getElementById('sticky-city-trigger');
    var closePopup = document.querySelector('.close-popup');

    var cityOptions = document.querySelectorAll('.city-option');

    var selectedCity = document.getElementById('selected-city');
    var selectedAddress = document.getElementById('selected-address');
    var headerSelectedCity = document.getElementById('header-selected-city');
    var stickySelectedCity = document.getElementById('sticky-selected-city');

    function openLocationPopup() {
        if (popup) {
            popup.classList.add('active');
        }
    }

    function closeLocationPopup() {
        if (popup) {
            popup.classList.remove('active');
        }
    }

    if (changeLocationBtn) {
        changeLocationBtn.addEventListener('click', openLocationPopup);
    }

    if (headerCityTrigger) {
        headerCityTrigger.addEventListener('click', openLocationPopup);
    }

    if (stickyCityTrigger) {
        stickyCityTrigger.addEventListener('click', openLocationPopup);
    }

    if (closePopup) {
        closePopup.addEventListener('click', closeLocationPopup);
    }

    function selectCity(cityId) {

        if (!window.cityDeliveryConfig[cityId]) {
            console.warn('Unknown city id, ignoring:', cityId);
            return;
        }

        var previousCityId = window.currentCityId;

        window.currentCityId = cityId;

        var cityName = window.cityDeliveryConfig[cityId].cityName;

        if (selectedCity) {
            selectedCity.textContent = cityName;
        }

        if (selectedAddress) {
            selectedAddress.textContent = cityName + ', India';
        }

        if (headerSelectedCity) {
            headerSelectedCity.textContent = cityName;
        }

        if (stickySelectedCity) {
            stickySelectedCity.textContent = cityName;
        }

        localStorage.setItem('selectedCityId', cityId);

        setCityCookie(cityName);

        console.log('Delivery city updated:', cityName);

        if (previousCityId && previousCityId !== cityId && cityLandingUrls[cityId]) {

            var redirectUrl = cityLandingUrls[cityId];

            if (window.location.search) {
                redirectUrl += window.location.search;
            }

            window.location.href = redirectUrl;
            return;
        }

        if (typeof window.saveDeliveryDetailsToCart === 'function') {
            window.saveDeliveryDetailsToCart();
        }

    }

    window.selectCity = selectCity;

    cityOptions.forEach(function (option) {

        option.addEventListener('click', function () {

            var cityId = this.dataset.cityId;

            closeLocationPopup();

            selectCity(cityId);

        });

    });

    (function initializeCity() {

        var availableCityIds = Object.keys(window.cityDeliveryConfig);

        if (availableCityIds.length === 0) {
            console.warn('No active cities found in city-delivery-config.');
            return;
        }

        var savedCityId = localStorage.getItem('selectedCityId');

        var initialCityId;

        if (savedCityId && window.cityDeliveryConfig[savedCityId]) {
            initialCityId = savedCityId;
        } else if (window.cityDeliveryConfig.bangalore) {
            initialCityId = 'bangalore';
        } else {
            initialCityId = availableCityIds[0];
        }

        selectCity(initialCityId);

    })();

});
